import {
  BalanceOfAbi,
  Erc4626ReadAbi,
  TotalSupplyAbi,
  VenusComptrollerRewardsAbi,
  VenusHubReadAbi,
  VenusResilientOracleAbi,
  VenusYieldGroupReadAbi,
  VTokenSizeReadAbi,
} from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * `maxDeposit(address)` on the Hub ignores its argument entirely (it returns
 * the aggregate spare capacity across unpaused YieldGroups), so the probe only
 * has to be a well-formed address. `0xdead` is the house convention — see
 * [withdrawLimit.ts](./withdrawLimit.ts).
 */
const NEUTRAL_PROBE = '0x000000000000000000000000000000000000dead'

/** Basis points → percent, as a float. Rates on this package are percent. */
const BPS_TO_PCT = 100

/**
 * Venus **Liquidity Hub** — a per-asset ERC-4626 allocator over three yield
 * families (Venus Core vTokens, Fluid fTokens, Venus Fixed-Rate Vaults).
 *
 * The Hub is a fully conforming ERC-4626 with `receiver`/`owner` on every leg
 * and zero fees, so it would *almost* work on the generic reader. Three facts
 * stop it, and each one is a call below.
 *
 * ## 1. A fifth of the vault cannot be withdrawn, and nothing standard says so
 *
 * The FRV family lends to institutions for a fixed term against escrowed
 * collateral. `AdapterFRV.maxWithdraw` returns **0 outside terminal states**:
 * the accrued coupon shows up in `totalAssets()` (and therefore in the share
 * price) while being undeliverable until the vault matures. On the live book
 * that is 851 k of vhUSDT's 4.26 M and 200 k of vhU's 851 k — so a row
 * publishing `liquidity: totalAssets` advertises a full exit that reverts for
 * any holder of more than ~80 % of the Hub.
 *
 * The Hub's own `maxWithdraw(owner)` computes exactly the right figure, but it
 * is clamped by the owner's balance, so a probe address reads 0 and it cannot
 * be used. Its aggregate — `_aggregateLiquid()` — is `internal`. This reader
 * therefore reproduces it: Hub idle (first-class liquidity, consumed before
 * any YieldGroup) plus each registered group's no-argument `maxWithdraw()`,
 * clamped by the per-transaction `maxWithdrawalSize()` and zeroed while
 * `hubPaused()`.
 *
 * The group roster is PINNED on the registry entry because a discovered
 * address cannot be a call target in the same multicall round. A group added
 * on-chain and not pinned here is simply not counted, which under-reports
 * liquidity — the safe direction — and
 * `test/vaults/venusHub.live.test.ts` fails on the divergence so it does not
 * stay that way.
 *
 * ## 2. There is no APY view, by design
 *
 * Venus's own docs say so: the Hub exposes none, and neither `api.venus.io`
 * nor DefiLlama carries a Hub pool. The rate is the TVL-weighted blend of the
 * groups' `spotAPYBps()`, which is a nominal APR in bps despite the name
 * (`AdapterCoreV1` = `supplyRatePerBlock × blocksPerYear`; `AdapterFlux` =
 * Fluid's pre-annualised `supplyRate + rewardsRate/1e10`) — already this
 * package's convention. It is computed here rather than in a `YieldFetcher`
 * because the weights ARE the same `totalAssets()` reads the row is built
 * from: splitting them across two RPC paths would blend a rate from one block
 * with weights from another, and buys nothing, since no off-chain source
 * exists to fall back to.
 *
 * ## 3. XVS emissions accrue to the vault and never reach a depositor
 *
 * See {@link SavingsVault.strandedRewardsRate}. `venusSupplySpeeds` is read on
 * the Core market so the row can state the size of that gap instead of
 * discovering it the day Venus turns emissions back on.
 *
 * Shares are **24-decimal over an 18-decimal asset** (`decimalsOffset = 6`),
 * so `decimals` / `underlyingDecimals` must both be set on the entry; the
 * generic exchange-rate normalisation below then holds unchanged.
 */
export const readerVenusHub = (entry: SavingsRegistryEntry): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  const cfg = entry.venusHub
  const groups = cfg?.yieldGroups ?? []
  // Matched by identity against `groups`, so the registry has to spell the
  // Core group the same way in both places — asserted by the live test.
  const coreGroup = cfg?.core?.yieldGroup

  const calls = [
    { address: entry.address, name: 'totalAssets', params: [] },
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'convertToAssets', params: [shareUnit] },
    { address: entry.address, name: 'maxDeposit', params: [NEUTRAL_PROBE] },
    { address: entry.address, name: 'hubPaused', params: [] },
    { address: entry.address, name: 'maxWithdrawalSize', params: [] },
    { address: entry.address, name: 'redeemFeeBps', params: [] },
    { address: entry.underlying, name: 'balanceOf', params: [entry.address] },
  ]
  const abis: unknown[] = [
    Erc4626ReadAbi,
    TotalSupplyAbi,
    Erc4626ReadAbi,
    VenusHubReadAbi,
    VenusHubReadAbi,
    VenusHubReadAbi,
    VenusHubReadAbi,
    BalanceOfAbi,
  ]

  // Three per group, in roster order: liquid funds, size, rate.
  for (const group of groups) {
    calls.push({ address: group, name: 'maxWithdraw', params: [] })
    calls.push({ address: group, name: 'totalAssets', params: [] })
    calls.push({ address: group, name: 'spotAPYBps', params: [] })
    abis.push(
      VenusYieldGroupReadAbi,
      VenusYieldGroupReadAbi,
      VenusYieldGroupReadAbi,
    )
  }

  // Emission leg. Six calls, all pinned addresses. Appended last so the
  // slice indices above stay independent of whether it is present.
  const emissionCallCount = cfg?.core ? 6 : 0
  if (cfg?.core) {
    const { yieldGroup, vToken, comptroller, oracle, rewardToken } = cfg.core
    calls.push({ address: yieldGroup, name: 'blocksPerYear', params: [] })
    calls.push({
      address: comptroller,
      name: 'venusSupplySpeeds',
      params: [vToken],
    })
    calls.push({ address: oracle, name: 'getPrice', params: [rewardToken] })
    calls.push({
      address: oracle,
      name: 'getUnderlyingPrice',
      params: [vToken],
    })
    calls.push({ address: vToken, name: 'totalSupply', params: [] })
    calls.push({ address: vToken, name: 'exchangeRateStored', params: [] })
    abis.push(
      VenusYieldGroupReadAbi,
      VenusComptrollerRewardsAbi,
      VenusResilientOracleAbi,
      VenusResilientOracleAbi,
      VTokenSizeReadAbi,
      VTokenSizeReadAbi,
    )
  }

  return {
    calls,
    abis,
    parse: (slice) => {
      const totalAssets = toBigInt(slice[0])
      const totalSupply = toBigInt(slice[1])
      const convertToAssetsRaw = toBigInt(slice[2])
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        convertToAssetsRaw === undefined
      ) {
        return undefined
      }

      const paused = slice[4] === true
      const maxWithdrawalSize = toBigInt(slice[5])
      const redeemFeeBps = toBigInt(slice[6])
      const idle = toBigInt(slice[7])

      // Entry side. `maxDeposit` already returns 0 while paused, so the
      // pause needs no second application here — and `0n` is a real answer
      // ("full, a deposit reverts"), never collapsed into "uncapped".
      const depositCapacity = toBigInt(slice[3])

      // ---- Per-group legs: liquidity and the rate blend -----------------
      // Both walk the same slots, so a group that fails to read contributes
      // to neither. Liquidity omitted entirely (rather than partial) when
      // any group read failed: a partial sum is a number that looks like an
      // answer, and `fetchPublic` renders an absent capacity as
      // `liquidity: 0`, which is the conservative reading.
      let groupLiquid = 0n
      let weightedBps = 0n
      let rateWeight = 0n
      // The Core group's OWN size — the emission leg's weight. Distinct from
      // `rateWeight`, which is every group summed.
      let coreGroupAssets = 0n
      let groupsOk = groups.length > 0
      for (let i = 0; i < groups.length; i++) {
        const base = 8 + i * 3
        const liquid = toBigInt(slice[base])
        const assets = toBigInt(slice[base + 1])
        const apyBps = toBigInt(slice[base + 2])
        if (
          liquid === undefined ||
          assets === undefined ||
          apyBps === undefined
        ) {
          groupsOk = false
          continue
        }
        if (groups[i] === coreGroup) coreGroupAssets = assets
        groupLiquid += liquid
        weightedBps += apyBps * assets
        rateWeight += assets
      }

      // Idle sits ahead of every group in `_routeWithdraw`, so it is part of
      // the same figure. The per-transaction cap binds because `liquidity`
      // means "what a holder can pull out this block" and one transaction is
      // what a holder gets.
      let instantRedeemCapacity: bigint | undefined
      if (groupsOk && idle !== undefined) {
        const aggregate = groupLiquid + idle
        instantRedeemCapacity =
          maxWithdrawalSize !== undefined && maxWithdrawalSize < aggregate
            ? maxWithdrawalSize
            : aggregate
      }

      // TVL-weighted nominal APR, in percent. Kept in integer space until the
      // last divide so a 24-decimal weight cannot lose the basis point.
      const supplyRate =
        rateWeight > 0n
          ? Number((weightedBps * 1_000_000n) / rateWeight) /
            1_000_000 /
            BPS_TO_PCT
          : undefined

      return {
        totalAssets,
        totalSupply,
        exchangeRate: (convertToAssetsRaw * ONE_E18) / underlyingUnit,
        ...(supplyRate !== undefined ? { supplyRate } : {}),
        ...(instantRedeemCapacity !== undefined
          ? {
              instantRedeemCapacity: paused ? 0n : instantRedeemCapacity,
              instantRedeemEnabled: !paused,
              // Retained in the vault rather than paid out, and 0 on every
              // live Hub — but it IS deducted from a redeemer's payout, so
              // it belongs on the same field every other two-legged row uses.
              withdrawFeeBps:
                redeemFeeBps !== undefined ? Number(redeemFeeBps) : 0,
            }
          : {}),
        ...(depositCapacity !== undefined ? { depositCapacity } : {}),
        ...(cfg?.core
          ? {
              strandedRewardsRate: parseStrandedRewards(
                slice,
                calls.length - emissionCallCount,
                coreGroupAssets,
                totalAssets,
              ),
            }
          : {}),
      }
    },
  }
}

/**
 * XVS supply-side emission APR on the Core market, scaled to the share of the
 * Hub actually sitting in it.
 *
 * `speed × blocksPerYear` XVS per year, priced by the Venus oracle, over the
 * market's total supplied USD, gives the rate a supplier in that market earns.
 * The Hub only holds part of its assets there, so the figure is weighted by
 * `coreGroupAssets / hubTotalAssets` — the same weighting `spotAPYBps` gets,
 * which is what makes the two comparable in a UI.
 *
 * Returns `undefined` when any input failed to read (never 0 — "we could not
 * tell" and "there are no emissions" are different statements, and this field
 * exists precisely to be watched for a change away from zero).
 */
const parseStrandedRewards = (
  slice: unknown[],
  base: number,
  coreGroupAssets: bigint,
  hubTotalAssets: bigint,
): number | undefined => {
  const blocksPerYear = toBigInt(slice[base])
  const supplySpeed = toBigInt(slice[base + 1])
  const rewardPrice = toBigInt(slice[base + 2])
  const underlyingPrice = toBigInt(slice[base + 3])
  const vTokenSupply = toBigInt(slice[base + 4])
  const exchangeRateStored = toBigInt(slice[base + 5])
  if (
    blocksPerYear === undefined ||
    supplySpeed === undefined ||
    rewardPrice === undefined ||
    underlyingPrice === undefined ||
    vTokenSupply === undefined ||
    exchangeRateStored === undefined
  ) {
    return undefined
  }
  if (supplySpeed === 0n) return 0

  // Both sides land in USD × 1e36; the scale cancels in the ratio, so it is
  // carried rather than divided out and the numerator keeps its precision.
  const rewardsPerYearUsd = supplySpeed * blocksPerYear * rewardPrice
  const suppliedRaw = (vTokenSupply * exchangeRateStored) / ONE_E18
  const suppliedUsd = suppliedRaw * underlyingPrice
  if (suppliedUsd === 0n || hubTotalAssets === 0n) return undefined

  // Two ratios, each below 1 and each taken at 1e9 to survive the divide.
  const marketAprPct =
    Number((rewardsPerYearUsd * 1_000_000_000n) / suppliedUsd) / 1e9
  const hubShare =
    Number((coreGroupAssets * 1_000_000_000n) / hubTotalAssets) / 1e9
  const scale = hubShare > 1 ? 1 : hubShare
  return marketAprPct * scale * 100
}
